/**
 * Session-launch builders — the pure decision logic behind the surfaces that
 * start a study session or a quick custom test (StudyMenu's By Topic list and
 * the Home continuation card on BOTH apps). Kept here so the free/premium cap
 * and slice/resume semantics can't drift across those entry points (they had
 * already been re-implemented independently in web + mobile).
 *
 * These return plain data; the apps map it onto their own navigation params /
 * test-runner fields.
 */

/**
 * The free/premium question caps for a quick custom test.
 * `free` MUST stay ≤ 5 to keep free users off the custom-test paywall.
 */
export const QUICK_CUSTOM_TEST_CAPS = { premium: 10, free: 5 };

/**
 * The quick-custom-test question cap for a user's tier — the single source read
 * by BOTH the launched test size (`buildQuickCustomTest`) and the continuation
 * card's copy math (`guidance/nextStep.js`), so the count the card promises
 * can't drift from the count the test contains.
 *
 * @param {boolean} isSubscribe premium?
 * @returns {number}
 */
export function quickCustomTestCap(isSubscribe) {
  return isSubscribe ? QUICK_CUSTOM_TEST_CAPS.premium : QUICK_CUSTOM_TEST_CAPS.free;
}

/**
 * The shared decision for a "quick custom test" launched from a nudge: the
 * free/premium question cap, the priority, and the topic scope. Apps spread
 * this and add their platform-specific runner fields (timing, display name).
 *
 * @param {Object} inputs
 * @param {string} inputs.priority       priority key (e.g. STRING.incorrect_tag or default)
 * @param {Array}  inputs.domainIds      topic ids to draw from (usually all domains)
 * @param {boolean} inputs.isSubscribe   premium?
 * @param {number} [inputs.numQuestions] exact question count to launch; clamped to the tier
 *   cap. The continuation card passes this (already `min(cap, incorrectPool)`) so the launched
 *   test matches the count the card promised without relying on the runner's pool-slicing.
 * @returns {{ test_name, num_questions, total_time, topic_ids, is_custom_test, priority }}
 */
export function buildQuickCustomTest({
  priority,
  domainIds,
  isSubscribe,
  numQuestions,
} = {}) {
  const cap = quickCustomTestCap(isSubscribe);
  return {
    test_name: 'Custom Test',
    // Clamp to the tier cap: an explicit numQuestions may shrink the test but can
    // NEVER push a free user past the paywall boundary (free ≤ 5). Rule 5 already
    // passes min(cap, pool); the clamp is the invariant's enforcement layer, so a
    // mis-wired caller can't ship a free user a larger test.
    num_questions: Math.min(numQuestions > 0 ? numQuestions : cap, cap),
    total_time: 0, // questions-mode: default per-question timer, no time-mode paywall
    topic_ids: [...(domainIds ?? [])],
    is_custom_test: true,
    priority,
  };
}

/**
 * Whether a resolved custom test is allowed to start. A priority pool
 * (incorrect / bookmarked / unanswered) can empty out between the moment a
 * nudge/menu computes its count and the moment the runner resolves the actual
 * set — most reliably on a fresh signup whose diagnostic scores are still
 * reconciling from Firestore, so the Home card can promise "N incorrect
 * questions" while the runner's `GET_CUSTOM_TEST_DETAILS` resolves to zero.
 * A 0-question test must NEVER initialize: the runners key their whole render
 * off `test_questions_meta_data`, so an empty set shows a permanent "Loading
 * question…" spinner (web) / blank body (mobile) with no way forward. Both
 * runners call this right after building the custom test and fall back to an
 * empty state instead of initializing.
 *
 * @param {Array} testQuestionsMetaData resolved test_questions_meta_data
 * @returns {boolean} true when there is at least one question to launch
 */
export function isCustomTestLaunchable(testQuestionsMetaData) {
  return Array.isArray(testQuestionsMetaData) && testQuestionsMetaData.length > 0;
}

/**
 * Copy for the empty-custom-test state (shown when isCustomTestLaunchable is
 * false). Kept in core so web + mobile render ONE source of truth — a future
 * wording tweak can't silently diverge across the two runners (the predicate is
 * already shared here; the copy belongs with it).
 *
 * @returns {{ title: string, message: string, backToHome: string }}
 */
export function getEmptyCustomTestCopy() {
  return {
    title: 'Nothing to review right now',
    message:
      "You don't have any questions to retake yet. Answer a few more questions and they'll show up here.",
    backToHome: 'Back to Home',
  };
}

/**
 * By-topic study-session parameters — single source for every surface that
 * launches a one-domain StudyQuestion session. Free tier serves the first
 * `domain.num_questions` bundled questions of the topic, flagged `free_model`.
 * `limit` caps the final set (quick session, applied after the free slice).
 * `resumeQuestionId` resumes at that question if it's in the set.
 *
 * @param {Object} inputs
 * @param {Object} inputs.domain           { id, domain_name, num_questions }
 * @param {Array}  inputs.questionsList     bundled exam questions [{ id, topic_id }]
 * @param {Array}  [inputs.questionsProgress] profile.questions_progress
 * @param {boolean} [inputs.freeTier]
 * @param {number} [inputs.limit]
 * @param {number|string} [inputs.resumeQuestionId]
 * @returns {{ set_id, questions, free_model, title, lastIndex, hasResumeProgress }}
 */
export function buildByTopicStudySession({
  domain,
  questionsList,
  questionsProgress,
  freeTier,
  limit,
  resumeQuestionId,
} = {}) {
  const setId = (domain?.id ?? 0) + 2;
  const topicQuestions = (questionsList ?? []).filter(
    (e) => String(e?.topic_id) === String(domain?.id)
  );

  let selected = freeTier ? topicQuestions.slice(0, domain?.num_questions) : topicQuestions;
  if (limit != null) selected = selected.slice(0, limit);

  const progress = (questionsProgress ?? []).filter(
    (obj) => obj?.study_questions_set_id == setId
  );
  const hasResumeProgress =
    progress.length > 0 &&
    selected.length > 0 &&
    selected[0].id != progress[0].last_seen_question_id;

  let lastIndex = 0;
  if (resumeQuestionId != null) {
    const idx = selected.findIndex((q) => String(q?.id) === String(resumeQuestionId));
    if (idx >= 0) lastIndex = idx;
  }

  return {
    set_id: setId,
    questions: selected,
    free_model: freeTier ? 1 : 0,
    title: domain?.domain_name,
    lastIndex,
    hasResumeProgress,
  };
}
